import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Calculator, Check, ExternalLink, Lock, Plus, Wallet } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ApiError, api } from '../lib/api';
import { adminUrl } from '../lib/apps';
import { P, useAuth } from '../lib/auth';
import { enumLabel, useI18n, type TranslationKey } from '../lib/i18n';
import { MONTH_NAMES, formatDate, formatDateTime, formatMoney, formatNumber } from '../lib/format';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  ErrorState,
  Field,
  Input,
  LoadingBlock,
  Metric,
  PageHeader,
  Select,
  Table,
  Td,
  Th,
  Tr,
  statusTone,
  useToast,
} from '../components/ui';

export function PayrollPage() {
  const { t, locale } = useI18n();
  const { can } = useAuth();
  const navigate = useNavigate();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['payroll', 'periods'],
    queryFn: () => api.get<any[]>('/payroll/periods'),
  });

  return (
    <>
      <PageHeader
        title={t('payroll.title')}
        action={
          can(P.PAYROLL_MANAGE) && (
            <Button variant="primary" icon={<Plus className="h-4 w-4" />} onClick={() => navigate('/payroll/new')}>
              {t('payroll.newPeriod')}
            </Button>
          )
        }
      />

      {can(P.PAYROLL_MANAGE) && (
        <Card padded={false}>
          <CardHeader title={t('payroll.periods')} className="p-5 pb-0" />
          {isLoading ? (
            <LoadingBlock />
          ) : error ? (
            <ErrorState error={error} onRetry={() => void refetch()} />
          ) : !data?.length ? (
            <EmptyState icon={<Wallet className="h-8 w-8" />} />
          ) : (
            <Table>
              <thead>
                <tr>
                  <Th>{t('common.month')}</Th>
                  <Th>{t('common.status')}</Th>
                  <Th align="end">{t('payroll.employees')}</Th>
                  <Th align="end">Generated</Th>
                </tr>
              </thead>
              <tbody>
                {data.map((period) => (
                  <Tr key={period.id} onClick={() => navigate(`/payroll/${period.id}`)}>
                    <Td>
                      <span className="tabular">
                        {MONTH_NAMES[locale][period.month - 1]} {period.year}
                      </span>
                    </Td>
                    <Td><Badge tone={statusTone(period.status)}>{enumLabel(period.status, locale)}</Badge></Td>
                    <Td align="end" className="tabular">{period._count.entries}</Td>
                    <Td align="end" className="text-caption text-muted">
                      {period.generatedAt ? formatDateTime(period.generatedAt, locale) : '—'}
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card>
      )}

      {/* An employee's own payslips (§36.4) are self-service, so they stayed
          in the admin, where everyone has an account; sys runs the periods. */}
    </>
  );
}

/** A worked day of the month not judged against its hours yet — `PAYROLL_DAYS_PENDING`'s `details.days`. */
interface WaitingDay {
  userId: string;
  name: string;
  nameAr: string | null;
  workDate: string;
  why: 'SHIFT_OPEN' | 'END_NOT_SET' | 'DAY_NOT_OVER';
  sessionId: string | null;
}

/**
 * Why the month cannot be calculated yet (operator, 2026-10-21: payroll waits for
 * every worked day to be judged — its penalties are not final until then). The
 * shifts are the admin's: each one still open, or forgotten, opens there.
 */
function WaitingDays({ days }: { days: WaitingDay[] }) {
  const { t, locale, localized } = useI18n();
  return (
    <Card className="mb-5 border-warning/40">
      <CardHeader title={t('payroll.daysWaitingTitle', { count: days.length })} subtitle={t('payroll.daysWaitingNote')} />
      <ul className="space-y-1.5 text-body-sm">
        {days.map((d) => (
          <li key={`${d.userId}-${d.workDate}`} className="flex flex-wrap items-center gap-x-3 gap-y-0.5">
            <span className="font-medium text-[var(--text-primary)]">{localized({ name: d.name, nameAr: d.nameAr })}</span>
            <span className="tabular text-muted">{formatDate(d.workDate, locale)}</span>
            <span className="text-warning-fg dark:text-warning-dark">{t(`payroll.waiting.${d.why}` as TranslationKey)}</span>
            {d.sessionId && (
              <a
                href={`${adminUrl()}/attendance/sessions/${d.sessionId}`}
                className="inline-flex items-center gap-1 text-caption text-primary-600 hover:underline dark:text-primary-400"
              >
                <ExternalLink className="h-3.5 w-3.5 flip-rtl" aria-hidden />
                {t('payroll.openShift')}
              </a>
            )}
          </li>
        ))}
      </ul>
    </Card>
  );
}

/** Overtime of the month not settled yet — `PAYROLL_OVERTIME_PENDING`'s `details.overtime`. */
interface WaitingOvertime {
  id: string;
  userId: string;
  name: string;
  nameAr: string | null;
  workDate: string;
  minutes: number;
  status: 'PENDING' | 'APPROVED';
}

/**
 * Why the month cannot be calculated yet (2026-10-21): what overtime pays is not
 * known until an administrator approves it and the employee accepts it. Overtime
 * is the admin's: each one opens there.
 */
function WaitingOvertimeList({ overtime }: { overtime: WaitingOvertime[] }) {
  const { t, locale, localized } = useI18n();
  return (
    <Card className="mb-5 border-warning/40">
      <CardHeader title={t('payroll.overtimeWaitingTitle', { count: overtime.length })} subtitle={t('payroll.overtimeWaitingNote')} />
      <ul className="space-y-1.5 text-body-sm">
        {overtime.map((o) => (
          <li key={o.id} className="flex flex-wrap items-center gap-x-3 gap-y-0.5">
            <span className="font-medium text-[var(--text-primary)]">{localized({ name: o.name, nameAr: o.nameAr })}</span>
            <span className="tabular text-muted">{formatDate(o.workDate, locale)}</span>
            <span className="text-warning-fg dark:text-warning-dark">{t(`payroll.overtimeWaiting.${o.status}` as TranslationKey)}</span>
            <a
              href={`${adminUrl()}/overtime/${o.id}`}
              className="inline-flex items-center gap-1 text-caption text-primary-600 hover:underline dark:text-primary-400"
            >
              <ExternalLink className="h-3.5 w-3.5 flip-rtl" aria-hidden />
              {t('payroll.openOvertime')}
            </a>
          </li>
        ))}
      </ul>
    </Card>
  );
}

/** A penalty of the month an employee objects to — `PAYROLL_OBJECTIONS_PENDING`'s `details.objections`. */
interface WaitingObjection {
  penaltyId: string;
  userId: string;
  name: string;
  nameAr: string | null;
  /** Days of salary; null on a penalty from before penalties were counted in days. */
  days: number | null;
  objectedAt: string | null;
}

/**
 * Why the month cannot be calculated yet (2026-10-22): an employee objects to an
 * approved penalty, and whether payroll deducts it is not known until an
 * administrator decides. Penalties are the admin's: each one opens there.
 */
function WaitingObjectionsList({ objections }: { objections: WaitingObjection[] }) {
  const { t, locale, localized } = useI18n();
  return (
    <Card className="mb-5 border-warning/40">
      <CardHeader
        title={t('payroll.objectionsWaitingTitle', { count: objections.length })}
        subtitle={t('payroll.objectionsWaitingNote')}
      />
      <ul className="space-y-1.5 text-body-sm">
        {objections.map((o) => (
          <li key={o.penaltyId} className="flex flex-wrap items-center gap-x-3 gap-y-0.5">
            <span className="font-medium text-[var(--text-primary)]">{localized({ name: o.name, nameAr: o.nameAr })}</span>
            {o.days != null && (
              <span className="tabular text-muted">{t('payroll.objectionDays', { days: formatNumber(o.days, locale) })}</span>
            )}
            {o.objectedAt && <span className="tabular text-muted">{formatDate(o.objectedAt, locale)}</span>}
            <span className="text-warning-fg dark:text-warning-dark">{t('payroll.objectionWaits')}</span>
            <a
              href={`${adminUrl()}/violations/penalties/${o.penaltyId}`}
              className="inline-flex items-center gap-1 text-caption text-primary-600 hover:underline dark:text-primary-400"
            >
              <ExternalLink className="h-3.5 w-3.5 flip-rtl" aria-hidden />
              {t('payroll.openPenalty')}
            </a>
          </li>
        ))}
      </ul>
    </Card>
  );
}

/** A day of the month more than an hour short, waiting for an administrator — `PAYROLL_SHORTFALL_PENDING`'s `details.shortDays`. */
interface WaitingShortDay {
  caseId: string;
  userId: string;
  name: string;
  nameAr: string | null;
  workDate: string;
  shortfallMinutes: number;
}

/** "2h 05m" / "2 س 05 د" — how much a day fell short. */
function hoursShort(minutes: number, locale: 'en' | 'ar'): string {
  const h = Math.floor(minutes / 60);
  const m = String(minutes % 60).padStart(2, '0');
  return locale === 'ar' ? `${h} س ${m} د` : `${h}h ${m}m`;
}

/**
 * Why the month cannot be calculated yet (CR-004): a day worked more than an hour
 * short costs what an administrator decides, and nothing until then. The days are
 * decided in the admin: each one opens there.
 */
function WaitingShortDaysList({ days }: { days: WaitingShortDay[] }) {
  const { t, locale, localized } = useI18n();
  return (
    <Card className="mb-5 border-warning/40">
      <CardHeader title={t('payroll.shortDaysWaitingTitle', { count: days.length })} subtitle={t('payroll.shortDaysWaitingNote')} />
      <ul className="space-y-1.5 text-body-sm">
        {days.map((d) => (
          <li key={d.caseId} className="flex flex-wrap items-center gap-x-3 gap-y-0.5">
            <span className="font-medium text-[var(--text-primary)]">{localized({ name: d.name, nameAr: d.nameAr })}</span>
            <span className="tabular text-muted">{formatDate(d.workDate, locale)}</span>
            <span className="tabular text-muted">{t('payroll.shortBy', { time: hoursShort(d.shortfallMinutes, locale) })}</span>
            <span className="text-warning-fg dark:text-warning-dark">{t('payroll.shortWaits')}</span>
            <a
              href={`${adminUrl()}/violations/short-days/${d.caseId}`}
              className="inline-flex items-center gap-1 text-caption text-primary-600 hover:underline dark:text-primary-400"
            >
              <ExternalLink className="h-3.5 w-3.5 flip-rtl" aria-hidden />
              {t('payroll.openShortDay')}
            </a>
          </li>
        ))}
      </ul>
    </Card>
  );
}

export function PayrollPeriodPage() {
  const { id = '' } = useParams();
  const { t, locale, localized } = useI18n();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [expanded, setExpanded] = useState<string | null>(null);

  const { data: period, isLoading, error, refetch } = useQuery({
    queryKey: ['payroll', 'period', id],
    queryFn: () => api.get<any>(`/payroll/periods/${id}`),
  });

  // The worked days of the month not judged yet, its overtime not settled, or the objections to
  // its penalties not decided, when calculating was refused for them (2026-10-21, 2026-10-22).
  const [waiting, setWaiting] = useState<WaitingDay[] | null>(null);
  const [waitingOvertime, setWaitingOvertime] = useState<WaitingOvertime[] | null>(null);
  const [waitingObjections, setWaitingObjections] = useState<WaitingObjection[] | null>(null);
  const [waitingShortDays, setWaitingShortDays] = useState<WaitingShortDay[] | null>(null);

  const act = useMutation({
    mutationFn: (action: string) => api.post<any>(`/payroll/periods/${id}/${action}`),
    onSuccess: (_result, action) => {
      setWaiting(null);
      setWaitingOvertime(null);
      setWaitingObjections(null);
      setWaitingShortDays(null);
      toast.success(`${action} ✓`);
      void queryClient.invalidateQueries({ queryKey: ['payroll'] });
    },
    onError: (e: any) => {
      const days = e instanceof ApiError && e.code === 'PAYROLL_DAYS_PENDING' ? (e.details as { days?: WaitingDay[] })?.days : null;
      const overtime =
        e instanceof ApiError && e.code === 'PAYROLL_OVERTIME_PENDING'
          ? (e.details as { overtime?: WaitingOvertime[] })?.overtime
          : null;
      const objections =
        e instanceof ApiError && e.code === 'PAYROLL_OBJECTIONS_PENDING'
          ? (e.details as { objections?: WaitingObjection[] })?.objections
          : null;
      setWaiting(days?.length ? days : null);
      setWaitingOvertime(overtime?.length ? overtime : null);
      setWaitingObjections(objections?.length ? objections : null);
      const shortDays =
        e instanceof ApiError && e.code === 'PAYROLL_SHORTFALL_PENDING'
          ? (e.details as { shortDays?: WaitingShortDay[] })?.shortDays
          : null;
      setWaitingShortDays(shortDays?.length ? shortDays : null);
      if (days?.length) {
        toast.error(t('payroll.daysWaiting', { count: days.length }));
        return;
      }
      if (overtime?.length) {
        toast.error(t('payroll.overtimeWaiting', { count: overtime.length }));
        return;
      }
      if (objections?.length) {
        toast.error(t('payroll.objectionsWaiting', { count: objections.length }));
        return;
      }
      if (shortDays?.length) {
        toast.error(t('payroll.shortDaysWaiting', { count: shortDays.length }));
        return;
      }
      toast.error(e.message);
    },
  });

  if (isLoading) return <LoadingBlock />;
  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;

  const locked = period.status === 'LOCKED';

  return (
    <>
      <div className="mb-4">
        <Link to="/payroll" className="inline-flex items-center gap-1.5 text-body-sm text-muted hover:text-[var(--text-primary)]">
          <ArrowLeft className="h-4 w-4 flip-rtl" aria-hidden />
          {t('payroll.title')}
        </Link>
      </div>

      <PageHeader
        title={`${MONTH_NAMES[locale][period.month - 1]} ${period.year}`}
        subtitle={<Badge tone={statusTone(period.status)}>{enumLabel(period.status, locale)}</Badge>}
        action={
          <>
            {['DRAFT', 'CALCULATED'].includes(period.status) && (
              <Button
                variant="primary"
                icon={<Calculator className="h-4 w-4" />}
                loading={act.isPending}
                onClick={() => act.mutate('calculate')}
              >
                {t('payroll.calculate')}
              </Button>
            )}
            {period.status === 'CALCULATED' && (
              <Button icon={<Check className="h-4 w-4" />} onClick={() => act.mutate('approve')}>
                {t('common.approve')}
              </Button>
            )}
            {period.status === 'APPROVED' && (
              <Button variant="signal" onClick={() => act.mutate('mark-paid')}>
                {t('payroll.markPaid')}
              </Button>
            )}
            {period.status === 'PAID' && (
              <Button icon={<Lock className="h-4 w-4" />} onClick={() => act.mutate('lock')}>
                {t('payroll.lock')}
              </Button>
            )}
          </>
        }
      />

      {locked && (
        <div className="mb-4 rounded-md border border-ai-plasma/30 bg-ai-plasma/8 px-4 py-2.5 text-body-sm text-ai-plasma-text dark:text-ai-plasma-lift">
          {t('payroll.lockedNote')}
        </div>
      )}

      {waiting && <WaitingDays days={waiting} />}
      {waitingOvertime && <WaitingOvertimeList overtime={waitingOvertime} />}
      {waitingObjections && <WaitingObjectionsList objections={waitingObjections} />}
      {waitingShortDays && <WaitingShortDaysList days={waitingShortDays} />}

      <div className="mb-5 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Metric label={t('payroll.employees')} value={formatNumber(period.employeeCount, locale)} />
        <Metric label={t('payroll.gross')} value={formatMoney(period.totals.gross, period.currency, locale)} />
        <Metric
          label={t('payroll.slaPenalties')}
          value={formatMoney(period.totals.penalties, period.currency, locale)}
          tone={period.totals.penalties > 0 ? 'warning' : 'neutral'}
        />
        <Metric
          label={t('payroll.net')}
          value={formatMoney(period.totals.net, period.currency, locale)}
          tone="success"
        />
      </div>

      <Card padded={false}>
        <Table>
          <thead>
            <tr>
              <Th>{t('nav.employees')}</Th>
              <Th align="end">{t('payroll.basic')}</Th>
              <Th align="end">{t('payroll.overtime')}</Th>
              <Th align="end">{t('payroll.nightAllowance')}</Th>
              <Th align="end">{t('payroll.slaPenalties')}</Th>
              <Th align="end">{t('payroll.net')}</Th>
              <Th align="end" />
            </tr>
          </thead>
          <tbody>
            {period.entries.map((entry: any) => (
              <>
                <Tr
                  key={entry.id}
                  onClick={() => setExpanded(expanded === entry.id ? null : entry.id)}
                >
                  <Td>
                    <span className="block">{localized(entry.user)}</span>
                    {entry.user.employeeCode && (
                      <span className="block font-mono text-caption text-faint">{entry.user.employeeCode}</span>
                    )}
                  </Td>
                  <Td align="end" className="tabular">{formatMoney(entry.basicSalary, entry.currency, locale)}</Td>
                  <Td align="end" className="tabular text-muted">
                    {formatMoney(entry.overtimeAmount, entry.currency, locale)}
                  </Td>
                  <Td align="end" className="tabular text-muted">
                    {formatMoney(entry.nightShiftAmount, entry.currency, locale)}
                  </Td>
                  <Td align="end" className={Number(entry.slaPenalties) > 0 ? 'tabular text-error-fg dark:text-error-dark' : 'tabular text-faint'}>
                    {Number(entry.slaPenalties) > 0
                      ? `−${formatMoney(entry.slaPenalties, entry.currency, locale)}`
                      : '—'}
                  </Td>
                  <Td align="end" className="tabular font-semibold">
                    {formatMoney(entry.netAmount, entry.currency, locale)}
                  </Td>
                  <Td align="end">
                    {!locked && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/payroll/${id}/entries/${entry.id}/adjust`);
                        }}
                      >
                        {t('payroll.adjustment')}
                      </Button>
                    )}
                  </Td>
                </Tr>

                {/* §20.4 — the line items are what makes a net figure explainable. */}
                {expanded === entry.id && (
                  <tr key={`${entry.id}-lines`}>
                    <td colSpan={7} className="border-b hairline bg-[var(--surface-sunken)] px-5 py-3">
                      <p className="mb-2 text-caption uppercase text-[var(--text-tertiary)]">
                        {t('payroll.lineItems')}
                      </p>
                      <ul className="space-y-1">
                        {entry.lineItems.map((line: any) => {
                          const isDeduction = ['SLA_PENALTY', 'OTHER_DEDUCTION'].includes(line.type);
                          return (
                            <li key={line.id} className="flex items-baseline justify-between gap-4 text-body-sm">
                              <span className="min-w-0">
                                <Badge
                                  tone={
                                    isDeduction
                                      ? 'error'
                                      : line.sourceType === 'WALLET_CASH_REWARD' || line.sourceType === 'DAY_REWARD'
                                        ? 'warning'
                                        : 'neutral'
                                  }
                                >
                                  {/* Cash an employee redeemed wallet points for, and a quarter or half day's pay an
                                      administrator granted (CR-004) — both paid with this salary. */}
                                  {line.sourceType === 'WALLET_CASH_REWARD'
                                    ? t('payroll.pointsReward')
                                    : line.sourceType === 'DAY_REWARD'
                                      ? t('payroll.dayReward')
                                      : enumLabel(line.type, locale)}
                                </Badge>
                                <span className="ms-2 text-muted">{line.description}</span>
                                {line.quantity && line.rate && (
                                  <span className="ms-2 text-caption text-faint tabular">
                                    {Number(line.quantity)} × {Number(line.rate)}
                                  </span>
                                )}
                              </span>
                              <span
                                className={
                                  'shrink-0 tabular ' + (isDeduction ? 'text-error-fg dark:text-error-dark' : 'text-[var(--text-primary)]')
                                }
                              >
                                {isDeduction ? '−' : '+'}
                                {formatMoney(line.amount, entry.currency, locale)}
                              </span>
                            </li>
                          );
                        })}
                      </ul>
                      <div className="mt-2 flex justify-between border-t hairline pt-2 text-body-sm font-semibold">
                        <span>{t('payroll.net')}</span>
                        <span className="tabular">{formatMoney(entry.netAmount, entry.currency, locale)}</span>
                      </div>
                    </td>
                  </tr>
                )}
              </>
            ))}
          </tbody>
        </Table>
      </Card>

    </>
  );
}
